// CometCafe カラーパレット（CLAUDE.md / コンセプトアート準拠・パステル）。
// 描画と将来の UI で共有する単一の正本。

export const PALETTE = {
  // 背景宇宙の流線
  spaceLavender: '#B8A8D8',
  spaceMint: '#A8D8C8',
  spacePink: '#F0C0D0',

  // カフェ本体
  cafePeach: '#F5C9A8',
  cafeCream: '#F5E8D8',
  cafeMintRoof: '#A8D5B8',

  // 発光・アクセント
  warmOrange: '#FFB870',
  crystalBlue: '#9DD6F0',

  // カフェ細部（コンセプトアート 2_cafe_b.png 準拠）
  cafeWindowGlow: '#FFCB7D', // 丸窓の暖色発光（主役）
  cafeWindowGlowCore: '#FFE6BE', // 丸窓の中心のいちばん明るい芯
  cafeTrim: '#BFE3CC', // ミントの庇・窓枠・見切り
  cafeDoor: '#E7C7E0', // 淡い藤色のドア
  cafeChimney: '#E8B89C', // 煙突（ピーチ濃いめ）
  cafeRoofDeck: '#CBBEE0', // 屋上デッキ（ラベンダー寄り）
  stringLight: '#FFE3A8', // 軒のストリングライト
  steam: '#F3ECF7', // 湯気・煙のパフ

  // 地面・小惑星
  asteroidTop: '#B7A6CE',
  asteroidSide: '#6F5E8E',
  gridLine: '#D8C8E8',
} as const;

export type PaletteKey = keyof typeof PALETTE;

/**
 * 属性 → スペシャルの光の色（v0.5.1）。「本日のスペシャル」を掲げている間、カフェが
 * その属性色の光を放つ（丸窓の発光ブレンド・上空への光柱・弱い PointLight）。
 * CLAUDE.md のパステルパレットに寄せた淡色。スペシャル未設定時は従来の暖色（下記 default）。
 */
export const SPECIAL_LIGHT_COLORS: Record<string, string> = {
  light: '#FFB870', // 光: ウォームオレンジ
  scent: '#F4B9D6', // 香り: ピンク
  water: '#9DD6F0', // 水辺: 水色
  calm: '#C9B8E8', // 静寂: ラベンダー
  lively: '#FFE3A8', // にぎやか: イエロー
  green: '#A8D98C', // 緑: ミントグリーン
  crystal: '#A8E0F2', // 結晶: クリスタルブルー
};

/** スペシャル未設定時の既定（従来の暖色＝丸窓のベース発光色に近い）。 */
export const SPECIAL_LIGHT_DEFAULT = '#FFB870';

/**
 * 昼夜（v0.6.0）。時間帯ごとの空グラデ 3 色・環境光色/強度・方向光強度・星の濃さ・
 * 発光ブースト。描画（diorama）が時間帯に応じてこれを適用する。
 *  - 朝: やわらかな桃＋クリームの明るい空。星はほぼ消える。
 *  - 昼: いちばん明るい。星は最も薄い。
 *  - 夕: オレンジ〜藤のたそがれ。発光が映え始める。
 *  - 夜: 紺〜紫の夜空。星が濃く、ランタン・窓・キノコの発光が際立つ（発光ブースト最大）。
 */
export interface TimeOfDaySky {
  /** 背景グラデの上・中・下（縦 3 色）。 */
  grad: [string, string, string];
  /** 環境光の色。 */
  ambient: string;
  /** 環境光の強度。 */
  ambientI: number;
  /** 方向光（キーライト）の強度。 */
  keyI: number;
  /** 星の不透明度（0..1）。 */
  starOpacity: number;
  /** 発光部のブースト（1.0=等倍。夜ほど強める）。 */
  glowBoost: number;
}

export const TIME_OF_DAY_SKY: Record<string, TimeOfDaySky> = {
  morning: {
    grad: ['#FBD9C2', '#F3C9DE', '#CDE0D8'],
    ambient: '#FFF0E0',
    ambientI: 0.9,
    keyI: 0.95,
    starOpacity: 0.12,
    glowBoost: 1.0,
  },
  day: {
    grad: ['#B8A8D8', '#A8D8C8', '#F0C0D0'],
    ambient: '#FFFFFF',
    ambientI: 0.95,
    keyI: 1.0,
    starOpacity: 0.06,
    glowBoost: 0.92,
  },
  dusk: {
    grad: ['#9A86C0', '#E0A6B8', '#FFC890'],
    ambient: '#F0D8E0',
    ambientI: 0.72,
    keyI: 0.7,
    starOpacity: 0.4,
    glowBoost: 1.25,
  },
  night: {
    grad: ['#2C2A50', '#3E3A6E', '#5A4A78'],
    ambient: '#9FB0E0',
    ambientI: 0.5,
    keyI: 0.42,
    starOpacity: 1.0,
    glowBoost: 1.6,
  },
};

/**
 * 季節（v0.6.0）。地面（小惑星上面）・背景ティント・木の葉の寄り色。描画が季節に
 * 応じてこれを乗算ティント等で適用する。
 *  - 春: 桜ピンク / 夏: 濃いミント / 秋: オレンジ / 冬: 雪白（地面うっすら白）。
 */
export interface SeasonPalette {
  /** 小惑星上面（地面）の色。 */
  ground: string;
  /** 背景グラデへ重ねるティント色。 */
  tint: string;
  /** ティントの強さ（0..1。背景・地面にどれだけ寄せるか）。 */
  tintStrength: number;
  /** 木の葉・草の寄り色（plant_mesh/deco の緑をこの色へ少し寄せる）。 */
  foliage: string;
}

export const SEASON_PALETTE: Record<string, SeasonPalette> = {
  spring: { ground: '#C9B6D0', tint: '#F6C9DD', tintStrength: 0.28, foliage: '#B7DDA0' },
  summer: { ground: '#9FC4B0', tint: '#79C9A6', tintStrength: 0.3, foliage: '#7CC36A' },
  autumn: { ground: '#C2A488', tint: '#E8A66A', tintStrength: 0.32, foliage: '#E0A24A' },
  winter: { ground: '#DCD8E6', tint: '#E6EEF6', tintStrength: 0.34, foliage: '#AEC8B8' },
};
